import { addScript, rejectAfter } from '@tunein/web-utils';
import { EventEmitter } from 'eventemitter3';
import { BLANK_MP3_DATA_URL } from '../../../constants';
import { ErrorName, ErrorType, PlayerError } from '../../../errors';
import { debug } from '../../../utils/logger';
import waitForGlobal from '../../../utils/waitForGlobal';
const SCRIPT_SRC = '//imasdk.googleapis.com/js/sdkloader/ima3.js';
const DEBUG_SCRIPT_SRC = '//imasdk.googleapis.com/js/sdkloader/ima3_debug.js';
const DEFAULT_VIDEO_WIDTH = 640;
const DEFAULT_VIDEO_HEIGHT = 360;
const ADS_LOAD_TIMEOUT = 5000;
const DEFAULT_PROVIDER_OPTIONS = {
    mediaElementId: 'web-media-player-ad-player',
    containerId: 'web-media-player-ad-container',
};
export class GoogleIMAProvider extends EventEmitter {
    static readyPromise = Promise.withResolvers();
    static #initStarted = false;
    static async init(debug = false) {
        if (this.#initStarted) {
            return;
        }
        this.#initStarted = true;
        try {
            await Promise.all([
                addScript({
                    src: debug ? DEBUG_SCRIPT_SRC : SCRIPT_SRC,
                    id: 'ima',
                    async: false,
                }),
                waitForGlobal('google'),
            ]);
            this.readyPromise.resolve();
        }
        catch {
            this.readyPromise.reject(new Error('Google IMA SDK loading failed'));
        }
    }
    #options;
    #isReady = false;
    #isInitializing = false;
    #adsLoaded = false;
    #isPaused = false;
    #adDisplayContainer;
    #adsLoader;
    #adsManager;
    #adManagerEvents;
    #mediaElement;
    #containerElement;
    #adRequestId;
    #duration;
    #currentTime;
    get name() {
        return 'google';
    }
    get isReady() {
        return this.#isReady;
    }
    get isInitializing() {
        return this.#isInitializing;
    }
    get volume() {
        return this.#adsManager?.getVolume() || 0;
    }
    get duration() {
        return this.#duration || 0;
    }
    get currentTime() {
        return this.#currentTime || 0;
    }
    get mediaElement() {
        return this.#mediaElement;
    }
    get containerElement() {
        return this.#containerElement;
    }
    constructor(options) {
        super();
        this.#options = {
            ...DEFAULT_PROVIDER_OPTIONS,
            ...options,
        };
    }
    playBlankMp3() {
        const { mediaElement } = this.#getAdElements();
        mediaElement.src = BLANK_MP3_DATA_URL;
        mediaElement.play();
    }
    async load(adRequestId, adRequestUrl) {
        this.#adRequestId = adRequestId;
        const { promise, resolve, reject } = Promise.withResolvers();
        const onReady = () => {
            this.#isReady = true;
            this.#isInitializing = false;
            this.off('aderror', onError);
            resolve();
        };
        const onError = () => {
            this.off('ready', onReady);
            reject(new Error('Ad load error. Refer to aderror event.'));
        };
        this.once('ready', onReady);
        this.once('aderror', onError);
        this.#applyImaSettings();
        this.#initAdsLoader();
        const adsRequest = this.#createAdsRequest(adRequestUrl);
        this.#adsLoader?.requestAds(adsRequest);
        await rejectAfter(promise, this.#options.adLoaderTimeout || ADS_LOAD_TIMEOUT, new PlayerError('Google IMA SDK ads loader timeout', google.ima.AdError.ErrorCode.VAST_LOAD_TIMEOUT, ErrorType.LOAD_ERROR, ErrorName.AdError, false));
    }
    setVolume(volume) {
        this.#adsManager?.setVolume(volume);
    }
    pause() {
        debug('Ad pause requested');
        if (this.#adsLoaded) {
            this.#isPaused = true;
            this.#adsManager?.pause();
        }
    }
    skip() {
        this.#adsManager?.skip();
    }
    play() {
        debug('Ad play requested', {
            isPaused: this.#isPaused,
            adsLoaded: this.#adsLoaded,
        });
        if (this.#isPaused) {
            this.#adsManager?.resume();
            this.#isPaused = false;
            return;
        }
        if (this.#adsLoaded) {
            return;
        }
        this.#adsLoaded = true;
        this.#adDisplayContainer?.initialize();
        try {
            this.#adsManager?.init(DEFAULT_VIDEO_WIDTH, DEFAULT_VIDEO_HEIGHT, google.ima.ViewMode.NORMAL);
            debug('Ad start requested');
            this.#adsManager?.start();
        }
        catch (error) {
            debug('Ad start error', error);
            this.#adsManager?.destroy();
        }
    }
    resizeVideo(width, height) {
        try {
            this.#containerElement.style.height = `${height}px`;
            this.#adsManager?.resize(width, height, google.ima.ViewMode.NORMAL);
        }
        catch {
        }
    }
    destroy() {
        this.#adsLoaded = false;
        this.#isPaused = false;
        this.#isReady = false;
        this.#isInitializing = false;
        this.#adRequestId = undefined;
        this.#deregisterAdsManagerListeners();
        this.#adsManager?.stop();
        this.#adsManager?.destroy();
        this.#adsLoader?.destroy();
        this.#adDisplayContainer?.destroy();
        this.#adsManager = undefined;
        this.#adsLoader = undefined;
        this.#adDisplayContainer = undefined;
        this.#containerElement?.removeAttribute('data-video');
        this.#mediaElement?.remove();
    }
    #initAdsLoader() {
        this.#isInitializing = true;
        if (!this.#options.containerId) {
            throw new Error('Missing containerId');
        }
        const { containerElement, mediaElement } = this.#getAdElements();
        this.#mediaElement = mediaElement;
        this.#containerElement = containerElement;
        this.#adDisplayContainer = new google.ima.AdDisplayContainer(this.#containerElement, this.#mediaElement);
        this.#createAdsLoader(this.#adDisplayContainer);
    }
    #applyImaSettings() {
        const { ppid, vpaidMode } = this.#options;
        if (ppid) {
            google.ima.settings.setPpid(ppid);
        }
        if (vpaidMode) {
            const VPAID_MODES = {
                disabled: google.ima.ImaSdkSettings.VpaidMode.DISABLED,
                enabled: google.ima.ImaSdkSettings.VpaidMode.ENABLED,
                insecure: google.ima.ImaSdkSettings.VpaidMode.INSECURE,
            };
            google.ima.settings.setVpaidMode(VPAID_MODES[vpaidMode]);
        }
    }
    #getAdElements() {
        const containerId = this.#options.containerId;
        const mediaElementId = this.#options.mediaElementId;
        let containerElement = document.getElementById(containerId);
        if (!containerElement) {
            containerElement = document.createElement('div');
            containerElement.id = containerId;
            document.body.appendChild(containerElement);
        }
        let mediaElement = document.getElementById(mediaElementId);
        if (!mediaElement) {
            mediaElement = document.createElement('video');
            mediaElement.id = mediaElementId;
            mediaElement.setAttribute('playsinline', '');
        }
        if (!containerElement.contains(mediaElement)) {
            containerElement.appendChild(mediaElement);
        }
        return { containerElement, mediaElement };
    }
    #createAdsLoader(adDisplayContainer) {
        if (!this.#adsLoader) {
            this.#adsLoader = new google.ima.AdsLoader(adDisplayContainer);
        }
        this.#registerAdsLoaderListeners();
    }
    #registerAdsLoaderListeners = () => {
        this.#adsLoader?.addEventListener(google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, this.#onAdsManagerLoaded, false);
        this.#adsLoader?.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, this.#onAdError('aderror'), false);
    };
    #createAdsRequest(adRequestUrl) {
        const adsRequest = new google.ima.AdsRequest();
        adsRequest.adTagUrl = adRequestUrl;
        adsRequest.setAdWillAutoPlay(true);
        adsRequest.setAdWillPlayMuted(false);
        adsRequest.linearAdSlotWidth = this.#mediaElement?.clientWidth || 0;
        adsRequest.linearAdSlotHeight = this.#mediaElement?.clientHeight || 0;
        adsRequest.nonLinearAdSlotWidth = this.#mediaElement?.clientWidth || 0;
        adsRequest.nonLinearAdSlotHeight = this.#mediaElement?.clientHeight || 0;
        return adsRequest;
    }
    #onAdsManagerLoaded = (event) => {
        if (!this.#mediaElement) {
            return;
        }
        this.#adsManager = event.getAdsManager(this.#mediaElement);
        this.#registerAdsManagerListeners();
        this.emit('ready');
    };
    #registerAdsManagerListeners() {
        this.#adManagerEvents = {
            [google.ima.AdEvent.Type.AD_BUFFERING]: this.#onAdEvent('adbuffering'),
            [google.ima.AdErrorEvent.Type.AD_ERROR]: this.#onAdError('aderror'),
            [google.ima.AdEvent.Type.AD_METADATA]: this.#onAdEvent('admetadata'),
            [google.ima.AdEvent.Type.AD_PROGRESS]: this.#onAdProgress,
            [google.ima.AdEvent.Type.ALL_ADS_COMPLETED]: this.#onAllAdsCompleted,
            [google.ima.AdEvent.Type.CLICK]: this.#onAdEvent('adclick'),
            [google.ima.AdEvent.Type.COMPLETE]: this.#onAdEvent('adcomplete'),
            [google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED]: this.#onContentPauseRequested,
            [google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED]: this.#onContentResumeRequested,
            [google.ima.AdEvent.Type.DURATION_CHANGE]: this.#onAdEvent('addurationchange'),
            [google.ima.AdEvent.Type.IMPRESSION]: this.#onAdEvent('adimpression'),
            [google.ima.AdEvent.Type.INTERACTION]: this.#onAdEvent('adinteraction'),
            [google.ima.AdEvent.Type.LOADED]: this.#onAdLoaded,
            [google.ima.AdEvent.Type.PAUSED]: this.#onAdPause,
            [google.ima.AdEvent.Type.RESUMED]: this.#onAdEvent('adresume'),
            [google.ima.AdEvent.Type.SKIPPED]: this.#onAdEvent('adskipped'),
            [google.ima.AdEvent.Type.STARTED]: this.#onAdEvent('adstart'),
            [google.ima.AdEvent.Type.VOLUME_CHANGED]: this.#onSimpleAdEvent('advolumechange'),
            [google.ima.AdEvent.Type.VOLUME_MUTED]: this.#onSimpleAdEvent('advolumemuted'),
        };
        Object.keys(this.#adManagerEvents).forEach((key) => {
            const handler = this.#adManagerEvents[key];
            if (handler) {
                this.#adsManager?.addEventListener(key, this.#adManagerEvents[key]);
            }
        });
    }
    #deregisterAdsManagerListeners() {
        if (!this.#adManagerEvents) {
            return;
        }
        Object.keys(this.#adManagerEvents).forEach((key) => {
            const handler = this.#adManagerEvents[key];
            if (handler) {
                this.#adsManager?.removeEventListener(key, this.#adManagerEvents[key]);
            }
        });
    }
    #emitAdEvent = (eventType, event) => {
        const ad = event.getAd();
        const adData = event.getAdData();
        const data = {
            adType: 'client',
            requestId: this.#adRequestId,
            adPosition: ad?.getAdPodInfo()?.getAdPosition(),
            contentType: ad?.getContentType(),
            creativeId: ad?.getCreativeId(),
            eventType,
            height: ad?.getHeight() || ad?.getVastMediaHeight(),
            id: ad?.getAdId(),
            totalAds: ad?.getAdPodInfo()?.getTotalAds(),
            width: ad?.getWidth() || ad?.getVastMediaWidth(),
            bitrate: adData?.vastMediaBitrate,
            destinationUrl: adData?.clickThroughUrl,
        };
        this.emit(eventType, data);
    };
    #onAdEvent = (eventType) => (event) => {
        this.#emitAdEvent(eventType, event);
    };
    #onSimpleAdEvent = (eventType) => () => {
        this.emit(eventType, { eventType });
    };
    #onAdError = (eventType) => (event) => {
        const error = event.getError();
        const errorData = {
            requestId: this.#adRequestId,
            message: error.getMessage(),
            name: error.name,
            errorCode: error.getErrorCode(),
            vastErrorCode: error.getVastErrorCode(),
            type: error.getType(),
            innerError: error.getInnerError(),
        };
        this.emit(eventType, errorData);
        this.destroy();
    };
    #onAdPause = (event) => {
        this.#isPaused = true;
        this.#emitAdEvent('adpause', event);
    };
    #onAdProgress = (event) => {
        const { duration, currentTime } = event?.getAdData() || {};
        this.#currentTime = currentTime;
        this.emit('adprogress', {
            adType: 'client',
            eventType: 'adprogress',
            duration: duration,
            currentTime: currentTime,
        });
    };
    #onAllAdsCompleted = () => {
        this.emit('alladscomplete');
        this.destroy();
    };
    #onAdLoaded = (event) => {
        const ad = event.getAd();
        const adData = event.getAdData();
        if (!ad) {
            this.#emitAdEvent('adloaded', event);
            return;
        }
        this.#duration = adData?.duration;
        const width = ad.getWidth() || DEFAULT_VIDEO_WIDTH;
        const height = ad.getHeight() || DEFAULT_VIDEO_HEIGHT;
        const isVideoAd = ad.getContentType()?.includes('video') || !!adData?.vpaid;
        this.#containerElement?.toggleAttribute('data-video', isVideoAd);
        if (isVideoAd) {
            this.#containerElement?.setAttribute('data-url', adData?.clickThroughUrl);
            this.resizeVideo(width, height);
        }
        if (!ad?.isLinear()) {
            this.#onContentResumeRequested(event);
        }
        this.#emitAdEvent('adloaded', event);
    };
    #onContentPauseRequested = () => {
        this.emit('contentpauserequested');
    };
    #onContentResumeRequested = (event) => {
        this.#emitAdEvent('contentresumerequested', event);
    };
}
